/**
 * Runtime self-report: what the app RESOLVED, readable without access to prod config.
 *
 * WHY THIS EXISTS (CAPA F273 follow-up, RD 2026-08-06):
 * Diagnosing the missing HIPAA countdown kept bottoming out in "check
 * REACT_APP_OIDC_ENABLED in the AWS secret bundle" — an answer that is useless to
 * someone who does not control the prod environment, which is the normal case. A
 * deployed build resolves its auth branch and its session controls from values nobody
 * on the clinical side can read, and then has no way to say what it decided.
 *
 * That is a DETECTABILITY gap, the same family as the gateway's F241: there was no
 * state in which the app announced whether a control measure was armed.
 *
 * This publishes the resolved state to `window.__atmanSession` and logs one line when it
 * changes. On any deployed environment, open devtools and read:
 *
 *   window.__atmanSession
 *   → { authProvider: 'okta', idleWarningArmed: true, ... }
 *
 * `idleWarningArmed` is the bottom line: whether an authenticated session currently has a
 * working inactivity warning + countdown behind it. It answers, from the browser, the
 * question that previously required reading a secret bundle.
 *
 * Diagnostics only — nothing here changes behaviour, and nothing here is a control. The
 * control is SessionInactivityGuard; this reports on it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SECURITY CONTRACT — THIS OBJECT IS PUBLIC. DO NOT WIDEN IT.
 *
 * `window.__atmanSession` is readable by any script on the page, and it ships in
 * production because production visibility is the entire point. It is safe ONLY because
 * every field is non-sensitive: booleans, two millisecond durations, and a provider name.
 *
 * NEVER add: tokens (Okta access/ID, the backend JWE), client IDs, issuer/authority URLs,
 * usernames, emails, patient identifiers, MRNs, or any PHI. A future field that carries a
 * credential converts a diagnostic into a disclosure.
 *
 * `sessionDiagnostics.test.ts` locks the published key set and asserts no value looks like
 * a credential, so widening this object fails a test rather than shipping quietly.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The complete set of keys allowed on the published object. Adding one is a decision. */
export const PUBLISHED_KEYS = [
  'authProvider',
  'guardMounted',
  'adapterRegistered',
  'adapterLabel',
  'authenticated',
  'inactivityThresholdMs',
  'warningDurationMs',
  'idleWarningArmed',
] as const

export interface SessionDiagnostics {
  /** Which auth branch App.tsx selected. */
  authProvider: 'okta' | 'oidc' | 'none'
  /** Is SessionInactivityGuard mounted at all? */
  guardMounted: boolean
  /** Has an auth branch registered a SessionAuthAdapter with it? */
  adapterRegistered: boolean
  /** Which one — a provider NAME only ('okta' / 'oidc'), never an identifier. */
  adapterLabel: string | null
  /** Does the adapter currently report an established session? */
  authenticated: boolean
  /** Effective idle threshold in ms (dev overrides applied). */
  inactivityThresholdMs: number | null
  /** Effective countdown duration in ms (dev overrides applied). */
  warningDurationMs: number | null
  /**
   * THE BOTTOM LINE: is an authenticated session currently protected by a working
   * inactivity warning + countdown? `false` here alongside `authenticated: true` is the
   * exact condition that produced the F273 report.
   */
  idleWarningArmed: boolean
}

const initial: SessionDiagnostics = {
  authProvider: 'none',
  guardMounted: false,
  adapterRegistered: false,
  adapterLabel: null,
  authenticated: false,
  inactivityThresholdMs: null,
  warningDurationMs: null,
  idleWarningArmed: false,
}

let current: SessionDiagnostics = { ...initial }

const computeArmed = (d: SessionDiagnostics): boolean =>
  d.guardMounted && d.adapterRegistered && d.authenticated

/** Read the current diagnostics (also mirrored on `window.__atmanSession`). */
export const getSessionDiagnostics = (): SessionDiagnostics => ({ ...current })

/**
 * Merge in what a caller knows and republish. Logs only when the armed state or the
 * provider actually changes, so this stays quiet during normal operation.
 */
export const publishSessionDiagnostics = (partial: Partial<SessionDiagnostics>): void => {
  const previous = current
  const next: SessionDiagnostics = { ...current, ...partial }
  next.idleWarningArmed = computeArmed(next)
  current = next

  // Publish ONLY the allow-listed keys — a caller passing extra fields cannot widen the
  // public surface by accident.
  const published: Record<string, unknown> = {}
  for (const key of PUBLISHED_KEYS) {
    published[key] = (next as any)[key]
  }

  try {
    ;(window as any).__atmanSession = published
  } catch {
    // Non-browser context (tests, SSR) — the in-memory copy is still readable.
  }

  const armedChanged = previous.idleWarningArmed !== next.idleWarningArmed
  const providerChanged = previous.authProvider !== next.authProvider
  if (!armedChanged && !providerChanged) {
    return
  }

  if (next.authenticated && !next.idleWarningArmed) {
    // The F273 condition, stated out loud rather than left to be inferred from config.
    console.warn(
      `[session] Authenticated on the "${next.authProvider}" branch with NO idle warning armed — ` +
        'this session can end with no countdown and no chance to stay signed in. ' +
        'Inspect window.__atmanSession.'
    )
    return
  }

  console.info(
    `[session] provider=${next.authProvider} idleWarningArmed=${next.idleWarningArmed}` +
      (next.adapterLabel ? ` adapter=${next.adapterLabel}` : '') +
      ' — inspect window.__atmanSession'
  )
}

/** Test seam. */
export const __resetSessionDiagnostics = (): void => {
  current = { ...initial }
  try {
    delete (window as any).__atmanSession
  } catch {
    /* ignore */
  }
}
